import { useState, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  parseKeywords,
  formatKeywords,
  isSameKeyword,
  KEYWORDS_MAX_CHARS,
} from '../utils/keywords';

const CHIP_BASE =
  'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-[6px] text-[11px] font-medium leading-tight shrink-0 transition-all';
const CHIP_ENABLED =
  'bg-[#43B02A]/10 text-[#2e8020] border border-[#43B02A]/25 hover:bg-[#43B02A]/20';
const CHIP_DISABLED =
  'bg-black/5 text-text-muted border border-border-default cursor-default';

/**
 * Chip/tag input for WhatsApp flow trigger keywords.
 *
 * Props
 * ─────
 * value     {string}   Comma-separated keywords string (the raw backend value).
 * onChange  {Function} Called with the new comma-separated string whenever chips change.
 * disabled  {boolean}  When true, renders chips read-only with no add/remove controls.
 * id        {string}   Optional id for the hidden text input (for label association).
 */
export function KeywordTagInput({
  value = '',
  onChange,
  disabled = false,
  id,
}) {
  const chips = parseKeywords(value);
  const [inputVal, setInputVal] = useState('');
  const inputRef = useRef(null);

  const commit = useCallback(
    (raw) => {
      const trimmed = raw.trim();
      if (!trimmed) return;
      const newChips = parseKeywords(trimmed); // handle pasted "a, b, c"
      const merged = [...chips];
      let duplicate = false;
      for (const chip of newChips) {
        if (merged.some((existing) => isSameKeyword(existing, chip))) {
          duplicate = true;
          continue;
        }
        merged.push(chip);
      }
      if (duplicate) toast.error('Keyword already added.');
      const serialised = formatKeywords(merged);
      if (serialised !== formatKeywords(chips)) {
        if (serialised.length <= KEYWORDS_MAX_CHARS) onChange?.(serialised);
      }
      setInputVal('');
    },
    [chips, onChange],
  );

  const removeChip = useCallback(
    (idx) => {
      const next = chips.filter((_, i) => i !== idx);
      onChange?.(formatKeywords(next));
    },
    [chips, onChange],
  );

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit(inputVal);
      return;
    }
    if (e.key === 'Backspace' && inputVal === '' && chips.length > 0) {
      removeChip(chips.length - 1);
    }
  };

  const handleBlur = () => {
    if (inputVal.trim()) commit(inputVal);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    commit(text);
  };

  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 w-full bg-white rounded-input px-3 py-2 min-h-[42px] border border-transparent shadow-sm transition-all ${
        !disabled
          ? 'focus-within:border-primary focus-within:ring-[3px] focus-within:ring-primary/15 cursor-text'
          : 'opacity-70 cursor-not-allowed'
      }`}
      onClick={() => !disabled && inputRef.current?.focus()}
    >
      {chips.map((chip, idx) => (
        <span
          key={`${chip}-${idx}`}
          className={`${CHIP_BASE} ${disabled ? CHIP_DISABLED : CHIP_ENABLED}`}
        >
          {chip}
          {!disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeChip(idx);
              }}
              className="w-3.5 h-3.5 rounded-full flex items-center justify-center hover:bg-[#43B02A]/20 transition-colors shrink-0 focus:outline-none"
              aria-label={`Remove keyword: ${chip}`}
            >
              <svg
                viewBox="0 0 10 10"
                className="w-2.5 h-2.5"
                fill="currentColor"
              >
                <path d="M6.414 5l2.293-2.293a1 1 0 0 0-1.414-1.414L5 3.586 2.707 1.293A1 1 0 0 0 1.293 2.707L3.586 5 1.293 7.293a1 1 0 1 0 1.414 1.414L5 6.414l2.293 2.293a1 1 0 0 0 1.414-1.414L6.414 5z" />
              </svg>
            </button>
          )}
        </span>
      ))}

      {!disabled && (
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onPaste={handlePaste}
          className="flex-1 min-w-[120px] bg-transparent outline-none text-sm text-text-primary placeholder:text-text-muted"
          placeholder={
            chips.length === 0
              ? 'Type keyword and press Enter…'
              : 'Add keyword…'
          }
          aria-label="Add trigger keyword"
        />
      )}

      {disabled && chips.length === 0 && (
        <span className="text-sm text-text-muted">No keywords configured</span>
      )}
    </div>
  );
}
